import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import { GameOutput } from '../generated-api';

interface GameSelectionWidgetProps {
  games: GameOutput[];
  selectedGameId: string;
  selectedGame: GameOutput | null;
  onGameSelect: (gameId: string) => void;
  readOnly?: boolean;
  /** Start with the selector expanded (default: false) */
  autoExpand?: boolean;
}

const GameSelectionWidget: React.FC<GameSelectionWidgetProps> = ({
  games,
  selectedGameId,
  selectedGame,
  onGameSelect,
  readOnly = false,
  autoExpand = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(autoExpand);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync with autoExpand prop
  useEffect(() => {
    if (autoExpand) {
      setIsExpanded(true);
    }
  }, [autoExpand]);

  // Filter games based on search query
  const filteredGames = games.filter(game =>
    game.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (game.tags && game.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))) ||
    (game.owners && game.owners.some(owner => owner.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
        setSearchQuery('');
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const handleGameSelect = (game: GameOutput) => {
    onGameSelect(game.id);
    setIsExpanded(false);
    setSearchQuery('');
  };

  const handleExpand = () => {
    if (!readOnly) {
      setIsExpanded(true);
    }
  };

  return (
    <div ref={containerRef} className="w-full">
      {/* Mobile: Full-screen modal */}
      <div className="md:hidden">
        {!isExpanded && (
          <button
            data-track="game_select_open"
            type="button"
            onClick={handleExpand}
            disabled={readOnly}
            data-testid="game-selection-button-mobile"
            className={`w-full text-left border rounded-lg p-4 transition-all ${
              readOnly
                ? 'bg-surface-base border-edge-input cursor-not-allowed'
                : 'bg-surface border-edge-input hover:border-primary-400 hover:shadow-md cursor-pointer'
            }`}
          >
            {selectedGame ? (
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {selectedGame.imageUrl ? (
                    <img
                      src={selectedGame.imageUrl}
                      alt={selectedGame.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-surface-skeleton flex items-center justify-center">
                      <span className="text-2xl">🎲</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-heading truncate">
                    {selectedGame.name}
                  </h3>
                </div>
                {!readOnly && (
                  <div className="flex-shrink-0">
                    <PencilIcon className="h-5 w-5 text-disabled" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-disabled">
                <div className="text-center">
                  <div className="text-4xl mb-2">🎲</div>
                  <div className="text-base font-medium">Tap to select a game...</div>
                </div>
              </div>
            )}
          </button>
        )}

        {isExpanded && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
            <div className="fixed inset-0 bg-surface flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-edge bg-surface">
                <h2 className="text-lg font-semibold text-heading">Select Game</h2>
                <button
                  type="button"
                  onClick={() => {
                    setIsExpanded(false);
                    setSearchQuery('');
                  }}
                  className="p-2 text-disabled hover:text-body rounded-full hover:bg-surface-active"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-4 border-b border-edge bg-surface">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-disabled" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search games by name, tag, or owner..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-edge-input rounded-lg focus:ring-primary-500 focus:border-primary-500 text-base"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 bg-surface-base">
                {filteredGames.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4" data-testid="game-selection-grid-mobile">
                    {filteredGames.map((game) => (
                      <button
                        data-track="game_select_option"
                        key={game.id}
                        type="button"
                        onClick={() => handleGameSelect(game)}
                        data-testid={`game-option-${game.id}`}
                        className={`relative bg-surface rounded-lg p-3 transition-all ${
                          selectedGameId === game.id
                            ? 'ring-2 ring-primary-500 shadow-lg'
                            : 'border border-edge'
                        }`}
                      >
                        {selectedGameId === game.id && (
                          <div className="absolute top-2 right-2 bg-primary-500 rounded-full p-1">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        <div className="aspect-square mb-2 rounded-md overflow-hidden bg-surface-subtle">
                          {game.imageUrl ? (
                            <img src={game.imageUrl} alt={game.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-4xl">🎲</div>
                          )}
                        </div>
                        <h3 className="text-sm font-semibold text-heading line-clamp-2 text-left mb-1">
                          {game.name}
                        </h3>
                        <div className="text-xs text-muted text-left space-y-1">
                          {game.minTeams && game.maxTeams && (
                            <div>
                              {game.minTeams === game.maxTeams
                                ? `${game.minTeams} teams`
                                : `${game.minTeams}-${game.maxTeams} teams`}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">🔍</div>
                    <h3 className="text-lg font-medium text-heading mb-2">No games found</h3>
                    <p className="text-muted">
                      {searchQuery ? `No games match "${searchQuery}"` : 'No games in your library yet'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Collapsible inline */}
      <div className="hidden md:block">
        {!isExpanded && (
          <button
            data-track="game_select_open"
            type="button"
            onClick={handleExpand}
            disabled={readOnly}
            data-testid="game-selection-button-desktop"
            className={`w-full text-left border rounded-lg p-4 transition-all ${
              readOnly
                ? 'bg-surface-base border-edge-input cursor-not-allowed'
                : 'bg-surface border-edge-input hover:border-primary-400 hover:shadow-md cursor-pointer'
            }`}
          >
            {selectedGame ? (
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {selectedGame.imageUrl ? (
                    <img
                      src={selectedGame.imageUrl}
                      alt={selectedGame.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-surface-skeleton flex items-center justify-center">
                      <span className="text-2xl">🎲</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-heading truncate">
                    {selectedGame.name}
                  </h3>
                  <div className="flex items-center space-x-3 mt-1">
                    {selectedGame.minTeams && selectedGame.maxTeams && (
                      <span className="text-sm text-muted">
                        {selectedGame.minTeams === selectedGame.maxTeams
                          ? `${selectedGame.minTeams} teams`
                          : `${selectedGame.minTeams}-${selectedGame.maxTeams} teams`}
                      </span>
                    )}
                    {selectedGame.owners && selectedGame.owners.length > 0 && (
                      <span className="text-sm text-muted">
                        👤 {selectedGame.owners.join(', ')}
                      </span>
                    )}
                  </div>
                  {selectedGame.tags && selectedGame.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedGame.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                        >
                          {tag}
                        </span>
                      ))}
                      {selectedGame.tags.length > 3 && (
                        <span className="text-xs text-muted">+{selectedGame.tags.length - 3} more</span>
                      )}
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <div className="flex-shrink-0">
                    <PencilIcon className="h-5 w-5 text-disabled" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-disabled">
                <div className="text-center">
                  <div className="text-4xl mb-2">🎲</div>
                  <div className="text-base font-medium">Click to select a game...</div>
                </div>
              </div>
            )}
          </button>
        )}

        {isExpanded && (
          <div className="border border-edge rounded-lg bg-surface">
            <div className="p-4 border-b border-edge">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-body">Select Game</h3>
                <button
                  type="button"
                  onClick={() => {
                    setIsExpanded(false);
                    setSearchQuery('');
                  }}
                  className="text-disabled hover:text-body"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-disabled" />
                </div>
                <input
                  type="text"
                  placeholder="Search games by name, tag, or owner..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-edge-input rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="p-4 bg-surface-base max-h-[500px] overflow-y-auto">
              {filteredGames.length > 0 ? (
                <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="game-selection-grid-desktop">
                  {filteredGames.map((game) => (
                    <button
                      data-track="game_select_option"
                      key={game.id}
                      type="button"
                      onClick={() => handleGameSelect(game)}
                      data-testid={`game-option-${game.id}`}
                      className={`relative bg-surface rounded-lg p-3 transition-all transform hover:scale-105 hover:shadow-lg ${
                        selectedGameId === game.id
                          ? 'ring-2 ring-primary-500 shadow-lg'
                          : 'border border-edge hover:border-primary-300'
                      }`}
                    >
                      {selectedGameId === game.id && (
                        <div className="absolute top-2 right-2 bg-primary-500 rounded-full p-1">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <div className="aspect-square mb-2 rounded-md overflow-hidden bg-surface-subtle">
                        {game.imageUrl ? (
                          <img src={game.imageUrl} alt={game.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-4xl">🎲</div>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-heading line-clamp-2 text-left mb-1">
                        {game.name}
                      </h3>
                      <div className="text-xs text-muted text-left space-y-1">
                        {game.minTeams && game.maxTeams && (
                          <div>
                            {game.minTeams === game.maxTeams
                              ? `${game.minTeams} teams`
                              : `${game.minTeams}-${game.maxTeams} teams`}
                          </div>
                        )}
                        {game.owners && game.owners.length > 0 && (
                          <div className="truncate">👤 {game.owners[0]}</div>
                        )}
                      </div>
                      {game.tags && game.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {game.tags.slice(0, 2).map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">🔍</div>
                  <h3 className="text-lg font-medium text-heading mb-2">No games found</h3>
                  <p className="text-muted">
                    {searchQuery ? `No games match "${searchQuery}"` : 'No games in your library yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameSelectionWidget;
